import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { LangContext, useLang } from '../../contexts/LangContext.js';
import {
  LineChart, BarChart, AreaChart, RadarChart, ScatterChart,
  Line, Bar, Area, Radar, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell, ComposedChart,
} from 'recharts';
import { COLORS_DARK, COLORS_LIGHT, FONT_SIZE, FONT_WEIGHT, RADIUS, SPACING } from './theme.js';
import { useTheme } from '../../contexts/ThemeContext.js';

// COLORS: LangContext/ThemeContext 없이도 동작하는 런타임 참조
// V49 메인의 isDark 상태에 따라 동적으로 결정되나,
// 탭 파일은 일단 COLORS_LIGHT를 기본값으로 사용
// (isDark props가 필요하면 추후 ThemeContext로 교체)

// LangContext — V49 메인의 Provider 안에서 렌더되므로 동작함

// ============================================================
// helpers.js
// EV Powertrain Simulator — Phase 4 컴포넌트 분리
// 공통 UI 헬퍼 컴포넌트 (입력, 툴팁, 차트 박스)
// ============================================================

// ── SHARED_PARAM_DB (inline copy from ev-sim_v1_0_Verification_49) ──
const SHARED_PARAM_DB = {
  "CdA": { n:{ko:"공기저항 계수×단면적", en:"Aero Drag Coeff×Area"}, s:"CdA", u:"m²", d:{ko:"variableCdA(v, params)로 속도·냉각기 개방 여부 반영. 고속 전비의 핵심 요인.", en:"Computed by variableCdA(v, params) with speed & cooling-grill effect. Dominant at high speed."} },
  "m": { n:{ko:"차량 질량", en:"Vehicle Mass"}, s:"m", u:"kg", d:{ko:"공차+적재(rlMassKg). 관성 항 m·a, F_roll = Crr·m·g, F_grade = m·g·sinθ 에 직결.", en:"Curb+payload (rlMassKg). Directly drives inertia m·a, F_roll, and F_grade."} },
  "Crr": { n:{ko:"구름저항 계수", en:"Rolling Resistance Coeff"}, s:"Crr", u:"-", d:{ko:"CrrDynamic(v, T)로 속도·온도 의존 보정. 저속·도심 전비에 지배적.", en:"Corrected by CrrDynamic(v, T) for speed and temperature. Dominates at low speed."} },
  "ρ": { n:{ko:"공기 밀도", en:"Air Density"}, s:"ρ", u:"kg/m³", d:{ko:"rhoCorrected(T_amb, alt) 단일 소스. 고도·온도를 동시 보정.", en:"Single source via rhoCorrected(T_amb, alt). Corrects for altitude and temperature."} },
  "grade_pct": { n:{ko:"경사도", en:"Road Grade"}, s:"grade_pct", u:"%", d:{ko:"F_grade = m·g·sin(θ). rlGrade 헤더 연동. 등반 소비 및 회생 회수량 결정.", en:"F_grade = m·g·sin(θ). Synced from rlGrade header. Determines climb load and regen recovery."} },
  "T_amb": { n:{ko:"외기온", en:"Ambient Temperature"}, s:"외기온", u:"°C", d:{ko:"공기밀도·Crr·HVAC 소비·배터리 내부저항에 동시 영향. 계절별 전비·항속 변화의 핵심 입력.", en:"Simultaneously affects air density, Crr, HVAC, and battery Ri. Key seasonal input."} },
  "battery_energy": { n:{ko:"배터리 에너지", en:"Battery Energy"}, s:"battery_energy", u:"kWh", d:{ko:"가용 에너지 기준. SOC 1% = battery_energy × 0.01 kWh. 항속거리 및 사이클 소모 계산 기준.", en:"Available energy basis. SOC 1% = battery_energy × 0.01 kWh. Base for range and cycle energy."} },
  "P_aux": { n:{ko:"보조부하", en:"Auxiliary Load"}, s:"P_aux", u:"W", d:{ko:"aux_power + hvacDetailedPower(). 전체 소비의 10~20%. 전비에 직접 가산. Phase 시간 × P_aux = 고정 소비.", en:"aux_power + hvacDetailedPower(). 10~20% of total. Added directly; Phase duration × P_aux."} },
  "soc_init": { n:{ko:"초기 SOC", en:"Initial SOC"}, s:"soc_init", u:"-", d:{ko:"사이클 시작 SOC. high/mid/low(1.0/0.85/0.65 × P_peak) 스케일 연동.", en:"SOC at cycle start. Scales P_peak by 1.0/0.85/0.65 for high/mid/low scenarios."} },
  "cold_derate_factor": { n:{ko:"저온 디레이팅 계수", en:"Cold Derate Factor"}, s:"cold_derate_factor", u:"-", d:{ko:"cold toggle ON 시 P_peak × factor 적용. params.cold_derate_factor (기본 0.72).", en:"When cold toggle ON: applies P_peak × factor. params.cold_derate_factor (default 0.72)."} },
  "gear_ratio": { n:{ko:"감속비 (기어비)", en:"Gear Ratio"}, s:"gear_ratio", u:"-", d:{ko:"T_wheel = T_motor × gear_ratio × η_dl. 발진 가속력·저항 토크·역기전력 결정.", en:"T_wheel = T_motor × gear_ratio × η_dl. Determines launch force, drag torque, and back-EMF."} },
  "T_max_motor": { n:{ko:"모터 최대 (피크) 토크", en:"Motor Peak Torque"}, s:"T_max_motor", u:"N·m", d:{ko:"저속 정토크 한계. 휠 출력 산정 기준. motorTorqueSpeed() 상한 및 교차점 결정.", en:"Constant torque limit at low speed. Ceiling for wheel output. Sets torque-speed crossover."} },
  "P_max_wheel": { n:{ko:"최대 휠 출력", en:"Max Wheel Power"}, s:"P_max_wheel", u:"W", d:{ko:"피크 출력 한계. P_avail_soc = P_peak × soc_k. SOC 스케일 상한. motorTorqueSpeed() 상한.", en:"Peak power limit. P_avail_soc = P_peak × soc_k. SOC-scaled upper bound."} },
  "P_cont_ratio": { n:{ko:"연속 출력 비율", en:"Continuous Power Ratio"}, s:"P_cont_ratio", u:"-", d:{ko:"params.P_cont_ratio (기본 0.70). P_cont = P_peak × ratio. 지속 주행 가능 출력 기준.", en:"params.P_cont_ratio (default 0.70). P_cont = P_peak × ratio. Sustainable driving power."} },
  "T_batt_derate_start": { n:{ko:"배터리 디레이팅 시작 온도", en:"Battery Derate Start Temp"}, s:"T_batt_derate_start", u:"°C", d:{ko:"params.T_batt_derate_start (기본 45°C). 이 온도 초과 시 방전 전력 선형 감소.", en:"params.T_batt_derate_start (default 45°C). Discharge power linearly reduced above this."} },
  "T_motor_derate_start": { n:{ko:"모터 디레이팅 시작 온도", en:"Motor Derate Start Temp"}, s:"T_motor_derate_start", u:"°C", d:{ko:"params.T_motor_derate_start (기본 140°C). 이 온도 초과 시 피크 토크 선형 감소.", en:"params.T_motor_derate_start (default 140°C). Peak torque linearly reduced above this."} },
  "C_th_batt/inv/motor": { n:{ko:"열용량 (배터리/인버터/모터)", en:"Thermal Capacity (Batt/Inv/Motor)"}, s:"C_th_batt/inv/motor", u:"J/K", d:{ko:"C_th_batt/inv/motor. 온도 상승률 dT/dt = Q/C_th 결정. 각 노드별 별도 설정.", en:"C_th per node. Determines temperature rise rate dT/dt = Q/C_th for each thermal node."} },
  "R0_base": { n:{ko:"배터리 기준 내부저항", en:"Battery Base Internal Resistance"}, s:"기준 내부저항", u:"Ω", d:{ko:"신품 기준. 저온에서 Arrhenius 역수로 급증. Ri(T) = R0 × (1+0.03·exp(-T/25)). 출력 전압 강하 유발.", en:"New cell baseline. Surges at low T via Arrhenius: Ri(T) = R0 × (1+0.03·exp(-T/25))."} },
  "P_cu": { n:{ko:"모터 동손", en:"Motor Copper Loss"}, s:"P_cu", u:"W", d:{ko:"3/2·Rs·Iq². 전류 제곱 비례. 저속 고부하 구간에서 지배적.", en:"3/2·Rs·Iq². Proportional to current squared. Dominates at low-speed high-load sections."} },
  "P_fe": { n:{ko:"모터 철손", en:"Motor Iron Loss"}, s:"철손", u:"W", d:{ko:"속도 비례 히스테리시스+와전류손. 회생 구간에서도 발생. 고속 손실의 주요 원인.", en:"Speed-proportional hysteresis + eddy current loss. Occurs even during regeneration."} },
  "μ": { n:{ko:"노면 마찰계수", en:"Road Friction Coeff"}, s:"μ", u:"-", d:{ko:"F_trac_lim = μ·(m+rlMassKg)·g·cosθ. 발진 접지한계선 및 Tornado 민감도 상위 항목.", en:"F_trac_lim = μ·(m+rlMassKg)·g·cosθ. Sets traction limit; top Tornado sensitivity factor."} },
  "soh_profile": { n:{ko:"셀 화학계 프로파일", en:"Cell Chemistry Profile"}, s:"soh_profile", u:"-", d:{ko:"nmc_622/811/lfp/nca. 4가지 열화 계수(k_cal, k_cyc, Ea, α)를 분기 선택.", en:"nmc_622/811/lfp/nca. Selects 4 degradation coefficients (k_cal, k_cyc, Ea, α)."} },
  "eta_regen_motor": { n:{ko:"회생 효율", en:"Regen Efficiency"}, s:"eta_regen_motor", u:"-", d:{ko:"Phase별 회생 에너지 회수율 결정. 회생 제동 시 운동에너지→전기에너지 변환 효율.", en:"Determines regenerative energy recovery rate per phase. Motor→electrical conversion efficiency."} },
  "S": { n:{ko:"심각도 (Severity)", en:"Severity"}, s:"S", u:"0-3", d:{ko:"인명 피해 심각도. S0(무피해)~S3(생명위협). ASIL 결정의 핵심 요소.", en:"Injury severity. S0(no harm)~S3(life-threatening). Key ASIL determination factor."} },
  "E": { n:{ko:"노출 빈도 (Exposure)", en:"Exposure"}, s:"E", u:"0-4", d:{ko:"운행 중 위험 상황 노출 빈도. E0(없음)~E4(항상). ASIL 매트릭스 행 결정.", en:"Frequency of hazard exposure. E0(none)~E4(always). Determines ASIL matrix row."} },
  "C": { n:{ko:"제어가능성 (Controllability)", en:"Controllability"}, s:"C", u:"0-3", d:{ko:"운전자의 사고 회피 능력. C0(회피가능)~C3(불가능). ASIL 매트릭스 열 결정.", en:"Driver's ability to avoid accident. C0(avoidable)~C3(impossible). Determines ASIL matrix column."} },
  "pf_v_max": { n:{ko:"최대 속도 한계", en:"Max Speed Threshold"}, s:"pf_v_max", u:"km/h", d:{ko:"시뮬레이션 실측 최고속도 vs 설정 한계 비교. Fault Injection 후 위반 여부 검증.", en:"Compares simulated peak speed vs threshold. Verifies violation after fault injection."} },
};



// ── makeT ──
function makeT(lang, i18n) {
  const dict = i18n || {};
  return (key) => (dict[key] ? (dict[key][lang] || dict[key].ko) : key);
}
// ── GraphInfoPopup ──
function GraphInfoPopup({ info, title, lang, onClose }) {
  const COLORS = useTheme();
  if (!info) return null;
  const L = lang || "ko";
  const monoFont = "'JetBrains Mono', 'Fira Mono', monospace";
  const accentColor = COLORS.accent2 || "#58a6ff";
  // ESC key handler
  React.useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{ position:"fixed", inset:0, zIndex:9000,
        background:"rgba(0,0,0,0.55)", backdropFilter:"blur(3px)",
        display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:COLORS.panel, border:`1.5px solid ${accentColor}60`,
          borderRadius:14, padding:"20px 24px", maxWidth:620, width:"100%",
          maxHeight:"80vh", overflowY:"auto", boxShadow:`0 8px 40px rgba(0,0,0,0.5)`,
          position:"relative" }}>
        {/* 닫기 버튼 */}
        <button onClick={onClose} style={{
          position:"absolute", top:12, right:14, background:"transparent",
          border:"none", cursor:"pointer", fontSize:18, color:COLORS.textMuted,
          lineHeight:1, padding:"2px 6px", borderRadius:4
        }}>✕</button>

        {/* 그래프 제목 */}
        <div style={{ fontSize:13, fontWeight:800, color:COLORS.text,
          marginBottom:14, paddingRight:28, lineHeight:1.4 }}>{title}</div>

        {/* 1. 목적 */}
        <div style={{ marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6 }}>
            <span style={{ fontSize:11, fontWeight:700, color:accentColor,
              background:accentColor+"20", borderRadius:4, padding:"1px 8px",
              fontFamily:monoFont }}>
              {L==="en"?"Purpose":"목적"}
            </span>
          </div>
          <div style={{ fontSize:11.5, color:COLORS.text, lineHeight:1.65,
            paddingLeft:4 }}>{L==="en" ? info.pur.en : info.pur.ko}</div>
        </div>

        {/* 2. 분석 이유 */}
        <div style={{ marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6 }}>
            <span style={{ fontSize:11, fontWeight:700, color:COLORS.accent3||"#3fb950",
              background:(COLORS.accent3||"#3fb950")+"20", borderRadius:4, padding:"1px 8px",
              fontFamily:monoFont }}>
              {L==="en"?"Why This Analysis":"분석 이유"}
            </span>
          </div>
          <div style={{ fontSize:11.5, color:COLORS.text, lineHeight:1.65,
            paddingLeft:4 }}>{L==="en" ? info.why.en : info.why.ko}</div>
        </div>

        {/* 3. 물리 파라미터 */}
        {info.prm && info.prm.length > 0 && (
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:8 }}>
              <span style={{ fontSize:11, fontWeight:700, color:COLORS.accent||"#f59e0b",
                background:(COLORS.accent||"#f59e0b")+"20", borderRadius:4, padding:"1px 8px",
                fontFamily:monoFont }}>
                {L==="en"?"Physics Parameters":"물리 파라미터"}
              </span>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {info.prm.map((p, i) => {
                // [Advance38] ref 해석: { ref: "sym" } → SHARED_PARAM_DB에서 전체 정의 로드
                // d_override가 있으면 그래프별 컨텍스트 설명으로 d 덮어씀
                const resolved = p.ref
                  ? { ...(SHARED_PARAM_DB[p.ref] || { n:{ko:p.ref,en:p.ref}, s:p.ref, u:"", d:{ko:"",en:""} }),
                      ...(p.d_override ? { d: p.d_override } : {}) }
                  : p;
                const pn = typeof resolved.n === "object"
                  ? (L==="en" ? resolved.n.en : resolved.n.ko)
                  : resolved.n;
                const pd = typeof resolved.d === "object"
                  ? (L==="en" ? resolved.d.en : resolved.d.ko)
                  : (resolved.d || "");
                return (
                  <div key={i} style={{ background:COLORS.bg, borderRadius:7,
                    border:`1px solid ${COLORS.border}`, padding:"7px 12px",
                    display:"grid", gridTemplateColumns:"auto 1fr", gap:"4px 12px",
                    alignItems:"start" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:11.5, fontWeight:700, color:COLORS.text,
                        fontFamily:monoFont }}>{resolved.s}</span>
                      {resolved.u && resolved.u !== "-" && (
                        <span style={{ fontSize:9, color:COLORS.textMuted,
                          background:COLORS.surface||COLORS.bg, borderRadius:3,
                          padding:"0px 5px", border:`1px solid ${COLORS.border}`,
                          fontFamily:monoFont }}>{resolved.u}</span>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize:10, fontWeight:600, color:COLORS.textDim,
                        marginBottom:2 }}>{pn}</div>
                      <div style={{ fontSize:10, color:COLORS.textMuted, lineHeight:1.5 }}>
                        {pd}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 4. 기법 정의 — tech 필드가 있는 DB 항목(MC·BMS·ESC·FOC·TIRE 등)에만 표시 */}
        {info.tech && (
          <div style={{ marginTop:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:8 }}>
              <span style={{ fontSize:11, fontWeight:700,
                color:"#a78bfa",
                background:"#a78bfa20", borderRadius:4, padding:"1px 8px",
                fontFamily:monoFont }}>
                {L==="en" ? "Method Definitions" : "기법 정의"}
              </span>
            </div>
            <div style={{ background:COLORS.bg, borderRadius:8,
              border:`1px solid #a78bfa40`, padding:"10px 14px" }}>
              {(L==="en" ? info.tech.en : info.tech.ko)
                .split("\n")
                .map((line, i) => {
                  const isBullet = line.startsWith("•");
                  const colonIdx = isBullet ? line.indexOf(":") : -1;
                  const term  = colonIdx > 0 ? line.slice(0, colonIdx+1) : null;
                  const desc  = colonIdx > 0 ? line.slice(colonIdx+1) : line;
                  return (
                    <div key={i} style={{
                      marginBottom: i < (L==="en" ? info.tech.en : info.tech.ko).split("\n").length-1 ? 8 : 0,
                      fontSize:10.5, lineHeight:1.6, color:COLORS.text,
                      paddingLeft: isBullet ? 0 : 12 }}>
                      {term
                        ? <><span style={{ fontWeight:700, color:"#a78bfa", fontFamily:monoFont }}>{term}</span><span style={{ color:COLORS.textMuted }}>{desc}</span></>
                        : <span style={{ color:COLORS.textMuted }}>{line}</span>
                      }
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}

        {/* 닫기 힌트 */}
        <div style={{ marginTop:14, fontSize:9, color:COLORS.textMuted, textAlign:"center" }}>
          {L==="en" ? "Click outside or press ESC to close" : "바깥 영역 클릭 또는 ESC 키로 닫기"}
        </div>
      </div>
    </div>
  );
}
// ── PI ──
function PI({ label, value, onChange, unit, step, min, max, desc }) {
  const COLORS = useTheme();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
      <label style={{ color: COLORS.textDim, fontSize: 11, flex: 1, minWidth: 0, fontFamily: mono }}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
        <input type="number" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          step={step || 1} min={min} max={max}
          style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.text,
            padding: "4px 6px", borderRadius: 4, minWidth: 60, maxWidth: 72, fontSize: 11, fontFamily: mono, outline: "none",
            transition: "border 0.15s" }}
          onFocus={(e) => e.target.style.borderColor = COLORS.accent}
          onBlur={(e) => e.target.style.borderColor = COLORS.border}
        />
        <span style={{ color: COLORS.textMuted, fontSize: 10, fontFamily: mono, width: 26, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{unit}</span>
      </div>
    </div>
  );
}
// ── PF ──
function PF({ k, label, type, step, min, max, up, params }) {
  const COLORS = useTheme();
  if (!up || !params) return null;
  const value = params[k];
  if (type === "bool") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <label style={{ color: COLORS.textDim, fontSize: 11, flex: 1, fontFamily: mono }}>{label}</label>
        <button onClick={() => up(k, !value)}
          style={{ padding: "3px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 700,
            background: value ? COLORS.accent3 : COLORS.bg,
            color: value ? "#000" : COLORS.textMuted,
            border: `1px solid ${value ? COLORS.accent3 : COLORS.border}` }}>
          {value ? "ON" : "OFF"}
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
      <label style={{ color: COLORS.textDim, fontSize: 11, flex: 1, minWidth: 0, fontFamily: mono }}>{label}</label>
      <input type="number" value={value ?? 0}
        onChange={(e) => up(k, parseFloat(e.target.value) || 0)}
        step={step || 1} min={min} max={max}
        style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.text,
          padding: "3px 6px", borderRadius: 4, minWidth: 60, maxWidth: 72, flexShrink: 0, fontSize: 11, fontFamily: mono, outline: "none" }}
        onFocus={(e) => e.target.style.borderColor = COLORS.accent}
        onBlur={(e) => e.target.style.borderColor = COLORS.border}
      />
    </div>
  );
}
// ── MC ──
function MC({ label, value, unit, color }) {
  const COLORS = useTheme();
  const display = value === null || value === undefined ? "N/R" : value;
  return (
    <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: 6,
      padding: "8px 12px", minWidth: 110, borderTop: `2px solid ${color || COLORS.accent}` }}>
      <div style={{ color: COLORS.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 1, marginBottom: 3, fontFamily: mono }}>{label}</div>
      <div style={{ color: color || COLORS.accent, fontSize: 18, fontWeight: 700, fontFamily: mono }}>
        {display}<span style={{ fontSize: 10, color: COLORS.textDim, marginLeft: 3 }}>{value !== null && value !== undefined ? unit : ""}</span>
      </div>
    </div>
  );
}
// ── CT ──
function CT({ active, payload, label, unitSuffix, decimals }) {
  const COLORS = useTheme();
  if (!active || !payload?.length) return null;
  const dp = decimals != null ? decimals : 2;
  return (
    <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`,
      borderRadius: 6, padding: "6px 10px", fontSize: 10, fontFamily: mono, maxHeight: 260, overflowY: "auto",
      boxShadow: "0 4px 12px rgba(0,0,0,0.25)" }}>
      <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 2 }}>
          <span style={{ color: COLORS.textMuted }}>{p.name}:</span>
          <span style={{ fontWeight: 600, color: p.color || COLORS.text }}>
            {typeof p.value === "number" ? p.value.toFixed(dp) : p.value}
            {unitSuffix || ""}
          </span>
        </div>
      ))}
    </div>
  );
}
// ── CB ──
function CB({ title, children, height, chartId }) {
  const COLORS = useTheme();
  return (
    <div style={{ background: "transparent", borderRadius: 0, padding: 0, border: "none", position: "relative" }}>
      {title && (
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: COLORS.textDim, fontFamily: mono }}>{title}</div>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height || 260}>{children}</ResponsiveContainer>
    </div>
  );
}
// ── SL ──
function SL({ text, color }) {
  const COLORS = useTheme();
  return <div style={{ fontSize: 11, fontWeight: 700, color: color || COLORS.accent, marginTop: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1, fontFamily: mono }}>{text}</div>;
}
// ── ECV ──
function ECV({ chain }) {
  const COLORS = useTheme();
  if (!chain) return null;
  const stages = [
    { label: "Batt", eta: chain.eta_batt, color: COLORS.battColor, icon: "🔋" },
    { label: "Inv", eta: chain.eta_inv, color: COLORS.invColor, icon: "⚡" },
    { label: "Motor", eta: chain.eta_motor, color: COLORS.motorColor, icon: "⚙️" },
    { label: "Gear", eta: chain.eta_gear, color: COLORS.gearColor, icon: "🔩" },
  ];
  // Compute total from stage product (robust fallback)
  const eta_product = +(stages.reduce((acc, s) => acc * ((s.eta || 90) / 100), 1) * 100).toFixed(1);
  const eta_display = (chain.eta_total && chain.eta_total > 1) ? chain.eta_total : eta_product;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center", flexWrap: "wrap", padding: 6 }}>
      {stages.map((st, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ background: `${st.color}15`, border: `1px solid ${st.color}40`, borderRadius: 6, padding: "5px 10px", textAlign: "center" }}>
            <div style={{ fontSize: 12 }}>{st.icon}</div>
            <div style={{ fontSize: 8, color: COLORS.textDim }}>{st.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: st.color, fontFamily: mono }}>{st.eta}%</div>
          </div>
          {i < 3 && <span style={{ color: COLORS.textMuted, fontSize: 10 }}>→</span>}
        </div>
      ))}
      <span style={{ color: COLORS.textMuted }}>=</span>
      <div style={{ background: `${COLORS.accent}15`, border: `2px solid ${COLORS.accent}60`, borderRadius: 6, padding: "5px 12px", textAlign: "center" }}>
        <div style={{ fontSize: 12 }}>🎯</div>
        <div style={{ fontSize: 8, color: COLORS.textDim }}>Total</div>
        <div style={{ fontSize: 18, fontWeight: 900, color: COLORS.accent, fontFamily: mono }}>{eta_display}%</div>
      </div>
    </div>
  );
}
// ── ModalSectionTitle ──
function ModalSectionTitle({ icon, title, color }) {
  const COLORS = useTheme();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, marginBottom: 10, paddingBottom: 6,
      borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: color || COLORS.accent, fontFamily: mono, letterSpacing: 0.5 }}>{title}</span>
    </div>
  );
}

// ── HdrBadge ─────────────────────────────────────────────────────
// 헤더 파라미터 현황 배지 컴포넌트
// direct[]  : 직접 영향 — 선명한 컬러 배지
// indirect[]: 간접/조건부 — 연한 컬러 배지 (opacity 낮춤)
// key 목록 : DM(드라이브모드) CY(주행사이클) MS(차중) SC(SOC)
//            GR(구배) RC(노면조건) TA(기온) MU(노면마찰) RH(공기밀도) WD(맞바람)
function HdrBadge({
  params = {}, lang = "ko", COLORS = {},
  rlMassKg = 0, rlGrade = 0, rlSocScenario = "high", rlHeadwind = 0,
  direct = [], indirect = [],
}) {
  const dm = (params.drive_mode || "NORMAL").toUpperCase();
  const dmColor = ({ ECO:"#22c55e", NORMAL:"#94a3b8", SPORT:"#ef4444", SNOW:"#38bdf8", OFF_ROAD:"#f97316" })[dm] || "#94a3b8";
  const gradeVal = rlGrade !== 0 ? rlGrade : +(Math.tan((params.grade_deg || 0) * Math.PI / 180) * 100).toFixed(1);
  const socPct   = typeof rlSocScenario === "number" ? rlSocScenario
    : ({ high: 80, mid: 60, low: 20 }[rlSocScenario] ?? Math.round((params.soc_init || 0.8) * 100));
  const socColor = socPct >= 70 ? "#16a34a" : socPct >= 40 ? "#2563eb" : "#dc2626";
  const rc = params.road_condition || "dry";
  const rcColor = ({ dry:"#16a34a", wet:"#2563eb", snow:"#64748b", ice:"#0ea5e9" })[rc] || "#16a34a";
  const rcLabel = lang === "ko"
    ? ({ dry:"🟢건조", wet:"🔵습윤", snow:"⚪적설", ice:"🧊빙판" })[rc]
    : ({ dry:"🟢Dry",  wet:"🔵Wet",  snow:"⚪Snow", ice:"🧊Ice"  })[rc];
  const windVal = rlHeadwind > 0 ? rlHeadwind : (params.wind_speed_ms || 0);

  const DEFS = {
    DM: { lbl: dm,                                              col: dmColor,   show: true },
    CY: { lbl: params.cycle_type || "WLTP",                    col: "#06b6d4", show: true },
    MS: { lbl: rlMassKg > 0 ? `+${rlMassKg}kg` : `${params.m || 1850}kg`, col: "#f97316", show: true },
    SC: { lbl: `SOC ${socPct}%`,                               col: socColor,  show: true },
    GR: { lbl: gradeVal >= 0 ? `▲${gradeVal.toFixed(1)}%` : `▼${Math.abs(gradeVal).toFixed(1)}%`, col: "#f59e0b", show: gradeVal !== 0 },
    RC: { lbl: rcLabel,                                        col: rcColor,   show: true },
    TA: { lbl: `${params.T_amb ?? 25}°C`,                      col: "#fb923c", show: true },
    MU: { lbl: `μ ${(params.mu ?? 1.0).toFixed(2)}`,           col: "#a78bfa", show: true },
    RH: { lbl: `ρ ${(params.rho ?? 1.225).toFixed(3)}`,        col: "#60a5fa", show: true },
    WD: { lbl: `💨${windVal}m/s`,                              col: "#06b6d4", show: windVal > 0 },
  };

  const mkSpan = (k, isDirect) => {
    const d = DEFS[k];
    if (!d || !d.show) return null;
    const c = d.col;
    return React.createElement("span", {
      key: k,
      style: {
        fontSize: 9, fontWeight: isDirect ? 700 : 500,
        padding: "2px 5px", borderRadius: 4, whiteSpace: "nowrap",
        background: c + (isDirect ? "22" : "11"),
        color: isDirect ? c : c,
        border: `1px solid ${c + (isDirect ? "55" : "28")}`,
        opacity: isDirect ? 1 : 0.6,
      },
    }, d.lbl);
  };

  const badges = [
    ...direct.map(k => mkSpan(k, true)),
    ...indirect.map(k => mkSpan(k, false)),
  ].filter(Boolean);

  if (badges.length === 0) return null;

  return React.createElement("div", {
    style: { marginLeft: "auto", display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" },
  }, ...badges);
}

// ============================================================
// ── SecTitle — 범용 탭 섹션 헤더 (C-2, C-4 대응)
// ──
// ── 목적:
// ──   • HWSafetyTabContent · RLTabContent 등이 각자 쓰던
// ──     fontWeight:700 + borderBottom:'none' 독자 패턴을 대체
// ──   • fontWeight는 FONT_WEIGHT.heading(500)으로 통일
// ──   • fontSize는 FONT_SIZE.section(12) 고정
// ──   • aft_helpers.jsx의 SectionHeader(AFT 전용)와 역할 분리
// ──
// ── Props:
// ──   title    {string|ReactNode}  헤더 텍스트 (필수)
// ──   C        {object}            COLORS 객체 (필수)
// ──   accent   {string=}           좌측 강조선 색상 (기본: '#f59e0b')
// ──   right    {ReactNode=}        우측 보조 콘텐츠 (배지·토글 등)
// ──   mb       {number=}           marginBottom (기본: SPACING.md = 12)
// ──
// ── 사용 예:
// ──   <SecTitle title="하드웨어 안전 분석" C={C} />
// ──   <SecTitle title="고장 모드" C={C} accent={'#f59e0b'4} right={<Badge />} />
// ============================================================
function SecTitle({ title, C, accent, right, mb }) {
  const borderColor = accent || '#f59e0b';
  return (
    <div style={{
      display:        'flex',
      justifyContent: 'space-between',
      alignItems:     'center',
      borderBottom:   `1px solid ${C.border}`,
      paddingBottom:  SPACING.xs,                    // 4px
      marginBottom:   mb !== undefined ? mb : SPACING.md,  // 12px
    }}>
      <div style={{
        display:    'flex',
        alignItems: 'center',
        gap:        SPACING.xs,                      // 4px
      }}>
        {/* 좌측 강조선 */}
        <span style={{
          display:      'inline-block',
          width:        3,
          height:       12,
          borderRadius: RADIUS.badge,                // 4px
          background:   borderColor,
          flexShrink:   0,
        }} />
        <span style={{
          fontSize:   FONT_SIZE.section,             // 12px
          fontWeight: FONT_WEIGHT.heading,           // 500
          color:      C.text,
          lineHeight: 1.3,
        }}>
          {title}
        </span>
      </div>
      {right && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
          {right}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ── RunButton — 범용 실행·다운로드 버튼 (W-3 대응)
// ──
// ── 목적:
// ──   • DOETabContent · EnhancedPhysicsTabContent 등
// ──     11개 파일에 흩어진 인라인 <button style={...}> 패턴을 대체
// ──   • DOETabContent의 S.btn() 팩토리와 동일한 ghost 스타일
// ──     (배경 투명 + 테두리 + 컬러) — 교체 시 시각 차이 없음
// ──   • disabled 상태 자동 처리 (불투명도 + 커서)
// ──
// ── Props:
// ──   onClick   {function}   클릭 핸들러 (필수)
// ──   label     {string|ReactNode}  버튼 텍스트 (필수)
// ──   C         {object}     COLORS 객체 (필수)
// ──   color     {string=}    강조 색상 (기본: '#f59e0b')
// ──   disabled  {boolean=}   비활성 여부 (기본: false)
// ──   size      {'sm'|'md'=} 크기 프리셋 (기본: 'md')
// ──   style     {object=}    추가 인라인 스타일 (mt 등 위치 보정용)
// ──
// ── 사용 예:
// ──   <RunButton onClick={runDOE} label="▶ 실행" C={C} disabled={running} />
// ──   <RunButton onClick={dlCSV}  label="⬇ CSV" C={C} color={'#f59e0b'3} size="sm" />
// ============================================================
function RunButton({ onClick, label, C, color, disabled, size, style: extraStyle }) {
  const col     = disabled ? (C.border || '#555') : (color || '#f59e0b');
  const isSmall = size === 'sm';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display:      'inline-flex',
        alignItems:   'center',
        gap:          SPACING.xs,                    // 4px
        fontSize:     isSmall ? FONT_SIZE.value : FONT_SIZE.sublabel,  // 10 or 11px
        fontWeight:   FONT_WEIGHT.heading,           // 500
        padding:      isSmall
                        ? `${SPACING.xs}px ${SPACING.sm}px`    // 4px 8px
                        : `${SPACING.xs + 2}px ${SPACING.md}px`, // 6px 12px
        borderRadius: RADIUS.button,                 // 6px
        border:       `1.5px solid ${col}`,
        background:   `${col}20`,
        color:        col,
        cursor:       disabled ? 'not-allowed' : 'pointer',
        opacity:      disabled ? 0.5 : 1,
        transition:   'opacity 0.15s',
        whiteSpace:   'nowrap',
        ...extraStyle,
      }}
    >
      {label}
    </button>
  );
}

export {
  makeT,
  GraphInfoPopup,
  PI,
  PF,
  MC,
  CT,
  CB,
  SL,
  ECV,
  ModalSectionTitle,
  HdrBadge,
  SecTitle,
  RunButton,
};